// src/components/admin/payroll/Payslip.js
import React, { useState, useEffect } from 'react';
import { X, Download, Printer } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import companySettingsService from '../../../services/companySettings.service';
import documentTemplateService from '../../../services/documentTemplate.service';
import { getProfessionalTax } from '../../../config/payrollConfig';
import professionalTaxService from '../../../services/professionalTax.service';
import pfService from '../../../services/pf.service';
import tdsService from '../../../services/tds.service';

const Payslip = ({
    employee,
    payrollMonth,
    onClose,
    // Pre-calculated salary data from PayrollRun (avoids recalculation)
    salaryData = null,
    // New props for OT, incentives, and attendance
    otData = null,
    incentives = [],
    workingDaysData = null,
    attendanceDecisions = null
}) => {
    const { theme } = useTheme();
    const [companySettings, setCompanySettings] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState('pay-standard'); // Default template
    const [computedPTMonthly, setComputedPTMonthly] = useState(0);
    const [computedPFMonthly, setComputedPFMonthly] = useState(0);
    const [computedTDSMonthly, setComputedTDSMonthly] = useState(0);
    const [pfExempted, setPfExempted] = useState(false);
    const [tdsExempted, setTdsExempted] = useState(false);
    const [deductionsLoading, setDeductionsLoading] = useState(true);
    const [companyState, setCompanyState] = useState(null); // null = not loaded yet

    // Load company settings and selected template from backend
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const settings = await companySettingsService.getSettings();
                setCompanySettings(settings);

                // Fetch company profile for state (used in PT calculation)
                const companyProfile = await companySettingsService.getCompanyProfile();
                setCompanyState(companyProfile?.state || '');
            } catch (error) {
                console.error('Error loading company settings:', error);
                setCompanyState(''); // Set to empty on error to allow deductions to proceed
            }
        };

        const fetchSelectedTemplate = async () => {
            const validTemplates = ['pay-standard', 'pay-detailed', 'pay-modern', 'pay-cognizant'];

            try {
                console.log('🔍 Fetching payslip template from database...');

                // Primary source: Backend database (works across all systems)
                const backendTemplates = await documentTemplateService.getSelectedTemplates();
                console.log('🌐 Backend templates:', backendTemplates);

                if (backendTemplates?.payslip && validTemplates.includes(backendTemplates.payslip)) {
                    console.log(`✅ Using database payslip template: ${backendTemplates.payslip}`);
                    setSelectedTemplate(backendTemplates.payslip);
                    // Cache to localStorage for offline/faster access
                    documentTemplateService.saveToLocalStorage(backendTemplates);
                    return;
                }

                // Fallback: localStorage cache (if backend fails or empty)
                console.log('⚠️ No template in database, checking localStorage...');
                const localTemplates = documentTemplateService.getLocalStorageTemplates();

                if (localTemplates?.payslip && validTemplates.includes(localTemplates.payslip)) {
                    console.log(`📦 Using cached localStorage template: ${localTemplates.payslip}`);
                    setSelectedTemplate(localTemplates.payslip);
                    return;
                }

                // Default fallback
                console.log('ℹ️ No template configured, using default: pay-standard');
                setSelectedTemplate('pay-standard');

            } catch (error) {
                console.error('❌ Error loading template from database:', error);

                // On error, try localStorage
                const localTemplates = documentTemplateService.getLocalStorageTemplates();
                if (localTemplates?.payslip && validTemplates.includes(localTemplates.payslip)) {
                    console.log(`📦 Database error, using cached template: ${localTemplates.payslip}`);
                    setSelectedTemplate(localTemplates.payslip);
                } else {
                    console.log('ℹ️ Using default template: pay-standard');
                    setSelectedTemplate('pay-standard');
                }
            }
        };

        fetchSettings();
        fetchSelectedTemplate();
    }, []);

    // Helper to safely get salary components as array
    const getSafeComponents = (emp) => {
        const components = emp?.salary_components || emp?.pay_package?.salary_components;
        // If it's already an array, return it; if it's a number (count), return empty array
        return Array.isArray(components) ? components : [];
    };

    // Fetch statutory deductions (PT, PF, TDS) from backend using async APIs
    // SKIP when salaryData is provided (pre-calculated values from PayrollRun)
    useEffect(() => {
        // Skip if salaryData is provided - no need to recalculate
        if (salaryData && salaryData.earningsBreakdown) {
            setDeductionsLoading(false);
            return;
        }

        // Wait for companyState to be loaded before calculating deductions
        if (companyState === null) {
            return; // Don't calculate yet - wait for company profile to load
        }

        const fetchStatutoryDeductions = async () => {
            setDeductionsLoading(true);
            try {
                // Calculate monthly gross and basic first
                const salaryComponents = getSafeComponents(employee)
                    .filter(comp => {
                        const name = (comp.name || '').toLowerCase();
                        return !name.includes('professional tax') &&
                               !name.includes('provident fund') &&
                               !name.includes('income tax') &&
                               !name.includes('tds');
                    });

                let monthlyEarnings = {};
                if (salaryComponents.length > 0) {
                    salaryComponents.forEach(comp => {
                        const monthlyAmount = comp.monthly_amount || (comp.amount / 12) || 0;
                        if (comp.type === 'earning') {
                            monthlyEarnings[comp.name] = monthlyAmount;
                        }
                    });
                } else {
                    const ctc = parseFloat(
                        employee.ctc ||
                        employee.annual_ctc ||
                        employee.pay_package?.ctc ||
                        employee.pay_package?.annual_ctc ||
                        0
                    );
                    const monthlyCTC = ctc / 12;
                    monthlyEarnings = {
                        'Basic Salary': monthlyCTC * 0.5,
                        'HRA (House Rent Allowance)': (monthlyCTC * 0.5) * 0.4,
                        'Special Allowance': monthlyCTC * 0.3
                    };
                }

                const monthlyGross = Object.values(monthlyEarnings).reduce((sum, val) => sum + val, 0);
                const basicSalary = monthlyEarnings['Basic Salary'] || (monthlyGross * 0.5);
                const daSalary = monthlyEarnings['Dearness Allowance'] || monthlyEarnings['DA'] || 0;
                const annualGross = monthlyGross * 12;

                // Convert payrollMonth to YYYY-MM format
                const monthYear = payrollMonth || 'JAN 2025';
                const [monthName, year] = monthYear.split(' ');
                const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
                const monthNum = monthNames.indexOf(monthName.toUpperCase()) + 1;
                const monthString = `${year}-${String(monthNum).padStart(2, '0')}`;

                // =====================================================
                // Fetch PT from backend
                // =====================================================
                try {
                    if (employee.id && monthlyGross > 0) {
                        const ptResult = await professionalTaxService.calculatePT(employee.id, monthlyGross, monthString);
                        setComputedPTMonthly(ptResult.ptAmount || 0);
                        console.log(`✅ Payslip PT from ${ptResult.source}: ₹${ptResult.ptAmount}`);
                    } else {
                        const ptSync = getProfessionalTax(companyState, monthlyGross);
                        setComputedPTMonthly(ptSync);
                    }
                } catch (ptError) {
                    console.error('PT calculation error:', ptError);
                    setComputedPTMonthly(getProfessionalTax(companyState, monthlyGross));
                }

                // =====================================================
                // Fetch PF from backend (with config check)
                // =====================================================
                try {
                    if (employee.id && basicSalary > 0) {
                        console.log(`🔍 Payslip calling PF API for Employee ${employee.employee_code || employee.id}...`);
                        const pfResult = await pfService.calculatePF(
                            employee.id,
                            basicSalary,
                            daSalary,
                            monthString
                        );

                        setComputedPFMonthly(pfResult.pfAmount || 0);
                        setPfExempted(pfResult.exemptionApplied || false);

                        if (pfResult.exemptionApplied) {
                            console.log(`ℹ️ Payslip PF Exempted: ${pfResult.exemptionReason || 'PF not applicable'}`);
                        } else if (pfResult.pfAmount > 0) {
                            console.log(`✅ Payslip PF from ${pfResult.source}: ₹${pfResult.pfAmount}/month`);
                        }
                    }
                } catch (pfError) {
                    console.error('❌ Payslip PF calculation error:', pfError);
                    // Set PF to 0 on error - safer than calculating blindly
                    setComputedPFMonthly(0);
                    setPfExempted(false);
                    console.warn('⚠️ Payslip PF set to ₹0 due to API error');
                }

                // =====================================================
                // Fetch TDS from backend (with config check)
                // =====================================================
                try {
                    if (employee.id && annualGross > 0) {
                        console.log(`🔍 Payslip calling TDS API for Employee ${employee.employee_code || employee.id}...`);
                        const currentFY = tdsService.getCurrentFinancialYear();
                        const tdsResult = await tdsService.calculateTDS(
                            employee.id,
                            annualGross,
                            currentFY
                        );

                        setComputedTDSMonthly(tdsResult.tdsAmount || 0);
                        setTdsExempted(tdsResult.exemptionApplied || false);

                        if (tdsResult.exemptionApplied) {
                            console.log(`ℹ️ Payslip TDS Exempted: ${tdsResult.exemptionReason || 'TDS not applicable'}`);
                        } else if (tdsResult.tdsAmount > 0) {
                            console.log(`✅ Payslip TDS from ${tdsResult.source}: ₹${tdsResult.tdsAmount}/month, Regime: ${tdsResult.taxRegime}`);
                        }
                    }
                } catch (tdsError) {
                    console.error('❌ Payslip TDS calculation error:', tdsError);
                    // Set TDS to 0 on error - safer than calculating blindly
                    setComputedTDSMonthly(0);
                    setTdsExempted(false);
                    console.warn('⚠️ Payslip TDS set to ₹0 due to API error');
                }

            } catch (error) {
                console.error('Error fetching statutory deductions for payslip:', error);
                // Fallback calculations on error
                const gross = Object.values(getSafeComponents(employee)
                    .filter(c => c.type === 'earning')
                    .reduce((acc, c) => ({ ...acc, [c.name]: c.monthly_amount || (c.amount / 12) }), {}))
                    .reduce((sum, val) => sum + val, 0);
                setComputedPTMonthly(getProfessionalTax(companyState, gross));
                setComputedPFMonthly(0); // Safe fallback
                setComputedTDSMonthly(0); // Safe fallback
            } finally {
                setDeductionsLoading(false);
            }
        };

        fetchStatutoryDeductions();
    }, [employee, payrollMonth, companyState, salaryData]);

    // Helper function to convert number to words
    const numberToWords = (num) => {
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

        const convertLessThanThousand = (n) => {
            if (n === 0) return '';
            if (n < 10) return ones[n];
            if (n < 20) return teens[n - 10];
            if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
            return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertLessThanThousand(n % 100) : '');
        };

        if (num === 0) return 'Zero';

        const crore = Math.floor(num / 10000000);
        const lakh = Math.floor((num % 10000000) / 100000);
        const thousand = Math.floor((num % 100000) / 1000);
        const remainder = num % 1000;

        let result = '';
        if (crore) result += convertLessThanThousand(crore) + ' Crore ';
        if (lakh) result += convertLessThanThousand(lakh) + ' Lakh ';
        if (thousand) result += convertLessThanThousand(thousand) + ' Thousand ';
        if (remainder) result += convertLessThanThousand(remainder);

        return result.trim();
    };

    // ========================================================================
    // USE PRE-CALCULATED SALARY DATA IF PROVIDED (from PayrollRun)
    // This avoids recalculation and ensures consistency with detailed view
    // ========================================================================

    // Get attendance info
    const workingDays = salaryData?.workingDays || workingDaysData?.workingDays || employee.workingDays || 22;
    const lop = salaryData?.lop || attendanceDecisions?.lopDays || employee.lop || 0;
    const leaves = employee.leaves || 0;
    const payableDays = workingDays - lop;

    // Calculate OT and Incentives totals
    const otPayAmount = otData?.otPay || 0;
    const incentivesTotal = Array.isArray(incentives)
        ? incentives.reduce((sum, inc) => sum + (parseFloat(inc.amount) || 0), 0)
        : 0;

    let proratedEarnings = {};
    let finalDeductions = {};
    let totalLOPDeduction = 0;
    let totalEarnings = 0;
    let totalDeductions = 0;
    let netSalary = 0;

    if (salaryData && salaryData.earningsBreakdown) {
        // ✅ USE PRE-CALCULATED DATA - No API calls, no recalculation
        console.log('✅ Payslip using pre-calculated salaryData from PayrollRun');

        // Use earnings breakdown directly
        proratedEarnings = { ...salaryData.earningsBreakdown };

        // Add OT pay if applicable
        if (otPayAmount > 0) {
            proratedEarnings['Overtime Pay'] = otPayAmount;
        }

        // Add incentives
        if (Array.isArray(incentives) && incentives.length > 0) {
            incentives.forEach(inc => {
                const name = inc.type || inc.name || 'Incentive';
                proratedEarnings[name] = parseFloat(inc.amount) || 0;
            });
        }

        // Use deductions breakdown directly
        finalDeductions = { ...salaryData.deductionsBreakdown };

        // Calculate totals
        totalLOPDeduction = salaryData.lopDeduction || 0;
        totalEarnings = Object.values(proratedEarnings).reduce((sum, val) => sum + val, 0);
        totalDeductions = Object.values(finalDeductions).reduce((sum, val) => sum + val, 0);
        netSalary = totalEarnings - totalDeductions;

        console.log(`📋 Payslip (Pre-calculated) for ${employee.employee_code || employee.id}:`);
        console.log(`   - Gross: ₹${salaryData.gross}`);
        console.log(`   - PF: ₹${salaryData.pf}`);
        console.log(`   - PT: ₹${salaryData.professionalTax}`);
        console.log(`   - Net: ₹${netSalary}`);

    } else {
        // ⚠️ FALLBACK: Calculate locally (when salaryData not provided)
        console.log('⚠️ Payslip calculating locally (no salaryData provided)');

        const salaryComponents = getSafeComponents(employee)
            .filter(comp => (comp.name || '').toLowerCase() !== 'professional tax');

        let monthlyEarnings = {};
        let monthlyDeductions = {};

        if (salaryComponents.length > 0) {
            salaryComponents.forEach(comp => {
                if ((comp.name || '').toLowerCase() === 'professional tax') {
                    return;
                }
                const monthlyAmount = comp.monthly_amount || (comp.amount / 12) || 0;
                if (comp.type === 'earning') {
                    monthlyEarnings[comp.name] = monthlyAmount;
                } else if (comp.type === 'deduction') {
                    monthlyDeductions[comp.name] = monthlyAmount;
                }
            });
        } else {
            const ctc = parseFloat(
                employee.ctc ||
                employee.annual_ctc ||
                employee.pay_package?.ctc ||
                employee.pay_package?.annual_ctc ||
                0
            );
            const monthlyCTC = ctc / 12;
            monthlyEarnings = {
                'Basic Salary': monthlyCTC * 0.5,
                'HRA (House Rent Allowance)': (monthlyCTC * 0.5) * 0.4,
                'Special Allowance': monthlyCTC * 0.3
            };
        }

        // Calculate LOP factor and prorate earnings
        const lopFactor = lop / workingDays;

        Object.keys(monthlyEarnings).forEach(key => {
            const fullAmount = monthlyEarnings[key];
            const deduction = fullAmount * lopFactor;
            proratedEarnings[key] = fullAmount - deduction;
            totalLOPDeduction += deduction;
        });

        // Add OT pay
        if (otPayAmount > 0) {
            proratedEarnings['Overtime Pay'] = otPayAmount;
        }

        // Add incentives
        if (Array.isArray(incentives) && incentives.length > 0) {
            incentives.forEach(inc => {
                const name = inc.type || inc.name || 'Incentive';
                proratedEarnings[name] = parseFloat(inc.amount) || 0;
            });
        }

        totalEarnings = Object.values(proratedEarnings).reduce((sum, val) => sum + val, 0);

        // Build deductions using computed state values
        const nonStatutoryDeductions = Object.fromEntries(
            Object.entries(monthlyDeductions).filter(([name]) => {
                const lowerName = (name || '').toLowerCase();
                return !lowerName.includes('professional tax') &&
                       !lowerName.includes('provident fund') &&
                       !lowerName.includes('pf') &&
                       !lowerName.includes('income tax') &&
                       !lowerName.includes('tds');
            })
        );
        finalDeductions = { ...nonStatutoryDeductions };

        // Add statutory deductions from computed state
        if (computedPFMonthly > 0 && !pfExempted) {
            finalDeductions['Provident Fund (PF)'] = computedPFMonthly;
        }
        if (computedPTMonthly > 0) {
            finalDeductions['Professional Tax'] = computedPTMonthly;
        }
        if (computedTDSMonthly > 0 && !tdsExempted) {
            finalDeductions['Income Tax (TDS)'] = computedTDSMonthly;
        }

        console.log(`📋 Payslip Deductions (Local calc) for ${employee.employee_code || employee.id}:`);
        console.log(`   - PF: ₹${computedPFMonthly} ${pfExempted ? '(Exempted)' : ''}`);
        console.log(`   - PT: ₹${computedPTMonthly}`);
        console.log(`   - TDS: ₹${computedTDSMonthly} ${tdsExempted ? '(Exempted)' : ''}`);

        totalDeductions = Object.values(finalDeductions).reduce((sum, val) => sum + val, 0);
        netSalary = totalEarnings - totalDeductions;
    }

    const netSalaryWords = numberToWords(Math.floor(netSalary));

    // Get month and year
    const monthYear = payrollMonth || 'AUG 2025';

    const handlePrint = () => {
        // Add print-specific styles temporarily
        const style = document.createElement('style');
        style.id = 'print-styles';
        style.innerHTML = `
            @page {
                margin: 0;
                size: A4;
            }
            @media print {
                /* Hide everything except payslip content */
                body > *:not(.fixed) {
                    display: none !important;
                }
                .fixed {
                    position: static !important;
                    background: white !important;
                }
                #payslip-content {
                    padding: 15mm 15mm 10mm 15mm !important;
                    margin: 0 !important;
                }
            }
        `;
        document.head.appendChild(style);

        // Print
        window.print();

        // Remove the temporary styles after printing
        setTimeout(() => {
            const printStyles = document.getElementById('print-styles');
            if (printStyles) {
                printStyles.remove();
            }
        }, 1000);
    };

    const handleDownload = () => {
        // Create a temporary window for printing with proper Tailwind CSS
        const printWindow = window.open('', '', 'width=800,height=600');
        const payslipContent = document.getElementById('payslip-content').innerHTML;

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
                <head>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <title>Payslip</title>

                    <!-- Tailwind CSS CDN -->
                    <script src="https://cdn.tailwindcss.com"></script>

                    <style>
                        /* Remove browser print headers/footers */
                        @page {
                            margin: 0;
                            size: A4;
                        }

                        @media print {
                            /* Hide browser headers/footers */
                            html, body {
                                margin: 0 !important;
                                padding: 0 !important;
                                background: white;
                                -webkit-print-color-adjust: exact;
                                print-color-adjust: exact;
                            }

                            #payslip-content {
                                max-width: none;
                                width: 100%;
                                margin: 0;
                                padding: 15mm 15mm 10mm 15mm; /* Reduced top padding */
                                page-break-after: avoid;
                            }

                            /* Hide page numbers, date, URL */
                            @page { margin: 0; }
                            body::before, body::after { display: none !important; }
                        }

                        body {
                            font-family: Arial, sans-serif;
                            background: white;
                            margin: 0;
                            padding: 0;
                        }

                        #payslip-content {
                            max-width: 210mm; /* A4 width */
                            margin: 0 auto;
                            padding: 15mm 15mm 10mm 15mm; /* Reduced top padding from 20mm to 15mm */
                            background: white;
                            color: #111827; /* text-gray-900 */
                        }
                    </style>
                </head>
                <body>
                    <div id="payslip-content">
                        ${payslipContent}
                    </div>
                    <script>
                        // Wait for Tailwind to load, then print
                        window.onload = function() {
                            setTimeout(function() {
                                window.print();
                            }, 500);
                        };
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    // Template rendering functions
    const renderStandardTemplate = () => (
        <div className="border-2 border-gray-300 p-6 rounded">
            {/* Header - Matches Document Builder preview */}
            <div className="text-center font-bold text-xl mb-4 pb-3 border-b-2 border-gray-900">
                SALARY SLIP
            </div>

            {/* Company Info */}
            <div className="flex justify-between items-start mb-4 pb-3 border-b border-gray-200">
                <div>
                    <p className="font-bold text-lg text-gray-900">{companySettings.name}</p>
                    <p className="text-sm text-gray-600">{companySettings.address?.street}</p>
                    <p className="text-sm text-gray-600">{companySettings.address?.city}, {companySettings.address?.state} {companySettings.address?.zipCode}</p>
                </div>
                {companySettings.logo && (
                    <img src={companySettings.logo} alt="Company Logo" className="h-16 w-auto object-contain" />
                )}
            </div>

            {/* Employee Details Grid - 2 column layout matching preview */}
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 pb-4 border-b border-gray-200 text-sm">
                <div className="flex justify-between">
                    <span className="text-gray-600">Employee Name:</span>
                    <span className="font-semibold text-gray-900">{employee.name || `${employee.first_name} ${employee.last_name}`}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-600">Employee ID:</span>
                    <span className="font-semibold text-gray-900">{employee.employee_code || employee.id || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-600">Designation:</span>
                    <span className="font-semibold text-gray-900">{employee.designation || employee.position || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-600">Department:</span>
                    <span className="font-semibold text-gray-900">{employee.department || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-600">Pay Period:</span>
                    <span className="font-semibold text-gray-900">{monthYear}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-600">PAN:</span>
                    <span className="font-semibold text-gray-900">{employee.pan_number || employee.panNumber || 'N/A'}</span>
                </div>
            </div>

            {/* Attendance Summary */}
            <div className="grid grid-cols-4 gap-4 mb-4 pb-4 border-b border-gray-200 text-sm">
                <div className="text-center">
                    <p className="text-gray-600">Working Days</p>
                    <p className="font-bold text-lg text-gray-900">{workingDays}</p>
                </div>
                <div className="text-center">
                    <p className="text-gray-600">Paid Leaves</p>
                    <p className="font-bold text-lg text-green-600">{leaves}</p>
                </div>
                <div className="text-center">
                    <p className="text-gray-600">LOP Days</p>
                    <p className="font-bold text-lg text-red-600">{lop}</p>
                </div>
                <div className="text-center">
                    <p className="text-gray-600">Payable Days</p>
                    <p className="font-bold text-lg text-blue-600">{payableDays}</p>
                </div>
            </div>

            {/* Earnings and Deductions - Side by Side matching preview */}
            <div className="grid grid-cols-2 gap-6 mb-4 pb-4 border-b border-gray-200">
                {/* Earnings */}
                <div>
                    <div className="font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-300">EARNINGS</div>
                    <div className="space-y-2 text-sm">
                        {Object.keys(proratedEarnings).map((componentName, index) => (
                            <div key={index} className="flex justify-between">
                                <span className="text-gray-700">{componentName}</span>
                                <span className="font-medium text-gray-900">₹{proratedEarnings[componentName].toFixed(2)}</span>
                            </div>
                        ))}
                        {lop > 0 && totalLOPDeduction > 0 && (
                            <div className="flex justify-between text-red-600 text-xs italic">
                                <span>Less: LOP ({lop} days)</span>
                                <span>-₹{totalLOPDeduction.toFixed(2)}</span>
                            </div>
                        )}
                        <div className="flex justify-between pt-2 border-t border-gray-300 font-bold text-gray-900">
                            <span>Gross Earnings</span>
                            <span>₹{totalEarnings.toFixed(2)}</span>
                        </div>
                    </div>
                </div>

                {/* Deductions */}
                <div>
                    <div className="font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-300">DEDUCTIONS</div>
                    <div className="space-y-2 text-sm">
                        {Object.keys(finalDeductions).length > 0 ? (
                            Object.keys(finalDeductions).map((deductionName, index) => (
                                <div key={index} className="flex justify-between">
                                    <span className="text-gray-700">{deductionName}</span>
                                    <span className="font-medium text-gray-900">₹{finalDeductions[deductionName].toFixed(2)}</span>
                                </div>
                            ))
                        ) : (
                            <div className="text-gray-500 italic">No deductions</div>
                        )}
                        <div className="flex justify-between pt-2 border-t border-gray-300 font-bold text-gray-900">
                            <span>Total Deductions</span>
                            <span>₹{totalDeductions.toFixed(2)}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Net Salary - Matching preview layout */}
            <div className="flex justify-between items-center font-bold text-lg py-3 px-4 bg-gray-100 rounded">
                <span>NET SALARY</span>
                <span className="text-xl">₹{netSalary.toFixed(2)}</span>
            </div>
            <div className="text-sm text-gray-600 mt-2 text-right italic">
                {netSalaryWords} Rupees Only
            </div>

            {/* Footer */}
            <div className="text-xs text-gray-500 mt-4 pt-3 border-t border-gray-200 space-y-1">
                <p>* This is a computer-generated payslip and does not require a signature.</p>
                <p>* All amounts are in {employee.currency || 'INR'}</p>
            </div>
        </div>
    );

    const renderDetailedTemplate = () => (
        <div className="border-2 border-gray-300 p-6 rounded">
            {/* Detailed Header - Matches Document Builder preview */}
            <div className="text-center mb-6 pb-4 border-b-2 border-gray-900">
                <h1 className="text-2xl font-bold mb-2 text-gray-900">DETAILED PAYSLIP</h1>
                <p className="text-sm text-gray-600">For the Month of {monthYear}</p>
            </div>

            {/* Company & Logo Row */}
            <div className="flex justify-between items-start mb-4">
                <div>
                    <p className="font-bold text-lg text-gray-900">{companySettings.name}</p>
                    <p className="text-sm text-gray-600">{companySettings.address?.street}</p>
                    <p className="text-sm text-gray-600">{companySettings.address?.city}, {companySettings.address?.state} {companySettings.address?.zipCode}</p>
                </div>
                {companySettings.logo && (
                    <img src={companySettings.logo} alt="Logo" className="h-16 w-auto" />
                )}
            </div>

            {/* Employee Details - 3 column grid with bg-gray-50 matching preview */}
            <div className="mb-4 p-3 rounded bg-gray-50">
                <div className="grid grid-cols-3 gap-3 text-xs">
                    <div><strong>Name:</strong> {employee.name || `${employee.first_name} ${employee.last_name}`}</div>
                    <div><strong>ID:</strong> {employee.employee_code || employee.id}</div>
                    <div><strong>PAN:</strong> {employee.pan_number || employee.panNumber || 'N/A'}</div>
                    <div><strong>Designation:</strong> {employee.designation || 'N/A'}</div>
                    <div><strong>Department:</strong> {employee.department || 'N/A'}</div>
                    <div><strong>UAN:</strong> {employee.uan || employee.uan_number || 'N/A'}</div>
                    <div><strong>Bank:</strong> {employee.bank_name || employee.bankName || 'N/A'}</div>
                    <div><strong>Account:</strong> {employee.account_number || employee.accountNumber || 'N/A'}</div>
                    <div><strong>Period:</strong> {monthYear}</div>
                </div>
            </div>

            {/* Attendance Summary - Compact */}
            <div className="mb-4 grid grid-cols-4 gap-2 text-center text-xs">
                <div className="p-2 bg-gray-100 rounded">
                    <p className="font-bold text-lg text-gray-900">{workingDays}</p>
                    <p className="text-gray-600">Working Days</p>
                </div>
                <div className="p-2 bg-green-50 rounded">
                    <p className="font-bold text-lg text-green-600">{leaves}</p>
                    <p className="text-gray-600">Paid Leaves</p>
                </div>
                <div className="p-2 bg-red-50 rounded">
                    <p className="font-bold text-lg text-red-600">{lop}</p>
                    <p className="text-gray-600">LOP Days</p>
                </div>
                <div className="p-2 bg-blue-50 rounded">
                    <p className="font-bold text-lg text-blue-600">{payableDays}</p>
                    <p className="text-gray-600">Payable Days</p>
                </div>
            </div>

            {/* Earnings & Deductions - Green/Red colored boxes matching preview */}
            <div className="grid grid-cols-2 gap-4 mb-4 text-xs">
                {/* Earnings with green background */}
                <div className="border p-3 rounded">
                    <div className="font-semibold mb-2 bg-green-100 p-1 rounded text-green-700">EARNINGS</div>
                    {Object.keys(proratedEarnings).map((comp, i) => (
                        <div key={i} className="flex justify-between py-1">
                            <span>{comp}</span>
                            <span className="font-medium">₹{proratedEarnings[comp].toFixed(2)}</span>
                        </div>
                    ))}
                    {lop > 0 && totalLOPDeduction > 0 && (
                        <div className="flex justify-between py-1 text-red-600 italic">
                            <span>Less: LOP ({lop} days)</span>
                            <span>-₹{totalLOPDeduction.toFixed(2)}</span>
                        </div>
                    )}
                    <div className="flex justify-between py-1 font-semibold border-t mt-1 pt-1">
                        <span>Gross Earnings</span>
                        <span>₹{totalEarnings.toFixed(2)}</span>
                    </div>
                </div>

                {/* Deductions with red background */}
                <div className="border p-3 rounded">
                    <div className="font-semibold mb-2 bg-red-100 p-1 rounded text-red-700">DEDUCTIONS</div>
                    {Object.keys(finalDeductions).length > 0 ? (
                        Object.keys(finalDeductions).map((ded, i) => (
                            <div key={i} className="flex justify-between py-1">
                                <span>{ded}</span>
                                <span className="font-medium">₹{finalDeductions[ded].toFixed(2)}</span>
                            </div>
                        ))
                    ) : (
                        <div className="text-gray-500 italic py-1">No deductions</div>
                    )}
                    <div className="flex justify-between py-1 font-semibold border-t mt-1 pt-1">
                        <span>Total Deductions</span>
                        <span>₹{totalDeductions.toFixed(2)}</span>
                    </div>
                </div>
            </div>

            {/* Net Pay - Blue highlighted matching preview */}
            <div className="flex justify-between items-center font-bold text-base bg-blue-50 p-3 rounded border-t-2 border-blue-500">
                <span>NET PAY</span>
                <span className="text-xl text-blue-700">₹{netSalary.toFixed(2)}</span>
            </div>
            <div className="text-xs mt-2 text-gray-600 italic text-right">
                In Words: {netSalaryWords} Rupees Only
            </div>

            {/* Footer */}
            <div className="text-xs text-gray-500 mt-4 pt-3 border-t border-gray-200 italic space-y-1">
                <p>* This is a computer-generated payslip and does not require a signature.</p>
                <p>* All amounts are in {employee.currency || 'INR'}</p>
            </div>
        </div>
    );

    const renderModernTemplate = () => (
        <div className="border-2 border-blue-500 p-6 rounded-lg bg-gradient-to-br from-blue-50 to-white">
            {/* Modern Header - Matches Document Builder preview with gradient */}
            <div className="flex justify-between items-center mb-6 pb-4 border-b-2 border-blue-200">
                <div>
                    <div className="text-2xl font-bold text-blue-600">{monthYear.split(' ')[0]}</div>
                    <div className="text-xs text-gray-600">Payslip for {monthYear.split(' ')[1] || monthYear}</div>
                </div>
                <div className="text-right">
                    <div className="text-xs text-gray-600">Net Salary</div>
                    <div className="text-2xl font-bold text-green-600">₹{Math.round(netSalary).toLocaleString()}</div>
                </div>
            </div>

            {/* Company Info */}
            <div className="mb-4 p-3 rounded-lg flex justify-between items-center bg-white shadow-sm">
                <div>
                    <p className="font-bold text-gray-900">{companySettings.name}</p>
                    <p className="text-xs text-gray-600">{companySettings.address?.city}, {companySettings.address?.state}</p>
                </div>
                {companySettings.logo && <img src={companySettings.logo} alt="Logo" className="h-12" />}
            </div>

            {/* Employee Info - 2 column with blue bullet matching preview */}
            <div className="mb-4 grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    <span><strong>{employee.name || `${employee.first_name} ${employee.last_name}`}</strong></span>
                </div>
                <div><strong>ID:</strong> {employee.employee_code || employee.id}</div>
                <div><strong>Designation:</strong> {employee.designation || 'N/A'}</div>
                <div><strong>Team:</strong> {employee.department || 'N/A'}</div>
            </div>

            {/* Attendance Summary - Compact cards */}
            <div className="mb-4 grid grid-cols-4 gap-2 text-center text-xs">
                <div className="p-2 bg-blue-100 rounded border border-blue-200">
                    <p className="font-bold text-lg text-blue-600">{workingDays}</p>
                    <p className="text-gray-600">Working</p>
                </div>
                <div className="p-2 bg-green-100 rounded border border-green-200">
                    <p className="font-bold text-lg text-green-600">{leaves}</p>
                    <p className="text-gray-600">Leaves</p>
                </div>
                <div className="p-2 bg-red-100 rounded border border-red-200">
                    <p className="font-bold text-lg text-red-600">{lop}</p>
                    <p className="text-gray-600">LOP</p>
                </div>
                <div className="p-2 bg-purple-100 rounded border border-purple-200">
                    <p className="font-bold text-lg text-purple-600">{payableDays}</p>
                    <p className="text-gray-600">Payable</p>
                </div>
            </div>

            {/* Earnings & Deductions - Card layout with shadows and emojis matching preview */}
            <div className="grid grid-cols-2 gap-3 mt-4">
                {/* Earnings Card */}
                <div className="bg-white p-3 rounded-lg shadow-sm border">
                    <div className="text-xs text-green-600 font-semibold mb-2">💰 EARNINGS</div>
                    <div className="space-y-1 text-xs">
                        {Object.keys(proratedEarnings).map((comp, i) => (
                            <div key={i} className="flex justify-between">
                                <span className="text-gray-700">{comp}</span>
                                <span className="font-medium">₹{Math.round(proratedEarnings[comp]).toLocaleString()}</span>
                            </div>
                        ))}
                        {lop > 0 && totalLOPDeduction > 0 && (
                            <div className="flex justify-between text-red-600 italic">
                                <span>Less: LOP</span>
                                <span>-₹{Math.round(totalLOPDeduction).toLocaleString()}</span>
                            </div>
                        )}
                        <div className="flex justify-between border-t pt-1 font-semibold text-green-600">
                            <span>Total</span>
                            <span>₹{Math.round(totalEarnings).toLocaleString()}</span>
                        </div>
                    </div>
                </div>

                {/* Deductions Card */}
                <div className="bg-white p-3 rounded-lg shadow-sm border">
                    <div className="text-xs text-red-600 font-semibold mb-2">📊 DEDUCTIONS</div>
                    <div className="space-y-1 text-xs">
                        {Object.keys(finalDeductions).length > 0 ? (
                            Object.keys(finalDeductions).map((ded, i) => (
                                <div key={i} className="flex justify-between">
                                    <span className="text-gray-700">{ded}</span>
                                    <span className="font-medium">₹{Math.round(finalDeductions[ded]).toLocaleString()}</span>
                                </div>
                            ))
                        ) : (
                            <div className="text-gray-500 italic">No deductions</div>
                        )}
                        <div className="flex justify-between border-t pt-1 font-semibold text-red-600">
                            <span>Total</span>
                            <span>₹{Math.round(totalDeductions).toLocaleString()}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Net Salary Summary */}
            <div className="mt-4 p-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg">
                <div className="flex justify-between items-center">
                    <span className="font-semibold">NET SALARY</span>
                    <span className="text-xl font-bold">₹{Math.round(netSalary).toLocaleString()}</span>
                </div>
                <div className="text-xs mt-1 opacity-90 text-right">
                    {netSalaryWords} Rupees Only
                </div>
            </div>

            {/* Modern Footer */}
            <div className="text-center text-xs text-gray-500 mt-4 space-y-1">
                <p>✓ Computer-generated payslip • No signature required</p>
                <p>All amounts in {employee.currency || 'INR'}</p>
            </div>
        </div>
    );

    const renderCognizantTemplate = () => {
        // ---------- Safe monthYear parsing ----------
        const toMonthYearText = (val) => {
            if (!val) return "";
            if (typeof val === "string") return val;

            if (val instanceof Date && !isNaN(val.getTime())) {
                return val.toLocaleString("en-US", { month: "short", year: "numeric" });
            }

            if (typeof val === "object") {
                const m = val.month ?? val.mm ?? val.monthName;
                const y = val.year ?? val.yy;
                if (m && y) {
                    if (typeof m === "number") {
                        const d = new Date(Number(y), Number(m) - 1, 1);
                        return d.toLocaleString("en-US", { month: "short", year: "numeric" });
                    }
                    return `${m} ${y}`;
                }
            }

            return String(val);
        };

        const monthYearText = toMonthYearText(monthYear);
        const parts = monthYearText.trim().split(/\s+/);
        const pdfMonth = (parts[0] || "").toUpperCase();
        const pdfYear = parts[1] || "";

        const formatINR = (n) =>
            Number(n || 0).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });

        // Address lines - handle object, array, or string
        const addressRaw = companySettings?.address;
        const addressText = Array.isArray(addressRaw)
            ? addressRaw.join("\n")
            : typeof addressRaw === "string"
                ? addressRaw
                : addressRaw && typeof addressRaw === "object"
                    ? [addressRaw.street, addressRaw.city, addressRaw.state, addressRaw.country, addressRaw.zipCode || addressRaw.postalCode]
                        .filter(Boolean)
                        .join(", ")
                    : "";

        const addressLines = addressText
            .split("\n")
            .map((l) => l.trim())
            .filter(Boolean);

        const earningsEntries = Object.entries(proratedEarnings || {});
        const deductionsEntries = Object.entries(finalDeductions || {});

        // ---------- Common styles ----------
        const S = {
            page: {
                fontFamily: "Arial, sans-serif",
                color: "#111",
                maxWidth: 980,
                margin: "0 auto",
                padding: 20,
                background: "#fff",
            },
            hr: { height: 1, background: "#111", border: "none", margin: "8px 0" },
            lightHr: { height: 1, background: "#d7d7d7", border: "none", margin: "0" },

            topRow: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 24,
            },
            title: {
                fontSize: 20,
                fontWeight: 700,
                letterSpacing: 0.2,
                lineHeight: 1.1,
            },
            titleLight: { fontWeight: 500, marginLeft: 8 },

            companyName: { fontSize: 14, fontWeight: 600, marginTop: 12 },
            companyAddr: { fontSize: 11, lineHeight: 1.6, marginTop: 8 },

            logoBox: {
                width: 200,
                height: 80,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-end",
            },
            logoImg: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" },

            empName: { fontSize: 14, fontWeight: 700, marginTop: 16 },

            // 4-col info grid with only row separators
            infoGrid: { marginTop: 8 },
            row4: {
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                columnGap: 24,
                padding: "10px 0",
            },
            label: { fontSize: 10, color: "#6a6a6a", marginBottom: 4 },
            value: { fontSize: 11, fontWeight: 500, color: "#111" },

            sectionTitle: { fontSize: 14, fontWeight: 600, marginTop: 16 },
            // salary row (4 columns) similar to info grid
            salaryRow: {
                display: "grid",
                gridTemplateColumns: "1.2fr 1.2fr 1fr 0.9fr",
                columnGap: 24,
                padding: "10px 0",
            },

            // earnings & deductions side-by-side
            splitWrap: {
                display: "grid",
                gridTemplateColumns: "1fr 1px 1fr",
                gap: 16,
                alignItems: "start",
                marginTop: 16,
            },
            vLine: { width: 1, background: "#d7d7d7", height: "100%" },

            blockTitle: { fontSize: 14, fontWeight: 700, marginBottom: 8 },
            list: { display: "flex", flexDirection: "column", gap: 6 },
            itemRow: { display: "flex", justifyContent: "space-between", gap: 12 },
            itemName: { fontSize: 11 },
            itemAmt: { fontSize: 11, fontVariantNumeric: "tabular-nums" },
            totalName: { fontSize: 11, fontWeight: 700 },
            totalAmt: { fontSize: 11, fontWeight: 700, fontVariantNumeric: "tabular-nums" },

            // net salary grey strip
            netBox: {
                background: "#f3f3f3",
                padding: "14px 20px",
                marginTop: 16,
            },
            netRow: { display: "grid", gridTemplateColumns: "1fr 180px", alignItems: "center" },
            netLabel: { fontSize: 13, fontWeight: 500 },
            netValue: { fontSize: 13, fontWeight: 500, textAlign: "left", fontVariantNumeric: "tabular-nums" },
            netWordsLabel: { fontSize: 12, fontWeight: 500, marginTop: 10 },
            netWordsValue: { fontSize: 12, fontWeight: 700, marginTop: 10 },

            notesWrap: { marginTop: 12 },
            noteLine: { fontSize: 10, display: "flex", gap: 12, alignItems: "baseline" },
            noteBold: { fontWeight: 700 },
            noteItalic: { fontStyle: "italic" },
            footerItalic: { fontSize: 10, fontStyle: "italic", marginTop: 16 },
        };

        return (
            <div style={S.page}>
                {/* TOP HEADER: Title + Logo */}
                <div style={S.topRow}>
                    <div style={{ flex: 1 }}>
                        <div style={S.title}>
                            PAYSLIP
                            <span style={S.titleLight}>
              {pdfMonth} {pdfYear}
            </span>
                        </div>

                        <div style={S.companyName}>{companySettings?.name || "COMPANY NAME"}</div>

                        {addressLines.length > 0 && (
                            <div style={S.companyAddr}>
                                {addressLines.map((line, idx) => (
                                    <div key={idx}>{line}</div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Logo at top-right (match screenshot).
            Put your logo url in companySettings.logo or companySettings.logoUrl */}
                    <div style={S.logoBox}>
                        {(companySettings?.logo || companySettings?.logoUrl) ? (
                            <img src={companySettings.logo || companySettings.logoUrl} alt="logo" style={S.logoImg} />
                        ) : null}
                    </div>
                </div>

                {/* Employee Name + Divider */}
                <div style={S.empName}>{employee?.employee_name || employee?.name || "EMPLOYEE NAME"}</div>
                <hr style={S.hr} />

                {/* Employee Info rows (only horizontal separators) */}
                <div style={S.infoGrid}>
                    <div style={S.row4}>
                        <div>
                            <div style={S.label}>Employee Number</div>
                            <div style={S.value}>{employee?.employee_code || employee?.id || "N/A"}</div>
                        </div>
                        <div>
                            <div style={S.label}>Date Joined</div>
                            <div style={S.value}>{employee?.joining_date || "N/A"}</div>
                        </div>
                        <div>
                            <div style={S.label}>Department</div>
                            <div style={S.value}>{employee?.department || "N/A"}</div>
                        </div>
                        <div>
                            <div style={S.label}>Sub Department</div>
                            <div style={S.value}>{employee?.sub_department || "N/A"}</div>
                        </div>
                    </div>
                    <hr style={S.lightHr} />

                    <div style={S.row4}>
                        <div>
                            <div style={S.label}>Designation</div>
                            <div style={S.value}>{employee?.designation || employee?.position || "N/A"}</div>
                        </div>
                        <div>
                            <div style={S.label}>Payment Mode</div>
                            <div style={S.value}>{employee?.payment_mode || "Bank Transfer"}</div>
                        </div>
                        <div>
                            <div style={S.label}>Bank</div>
                            <div style={S.value}>{employee?.bank_name || "N/A"}</div>
                        </div>
                        <div>
                            <div style={S.label}>Bank IFSC</div>
                            <div style={S.value}>{employee?.ifsc_code || "N/A"}</div>
                        </div>
                    </div>
                    <hr style={S.lightHr} />

                    <div style={S.row4}>
                        <div>
                            <div style={S.label}>Bank Account</div>
                            <div style={S.value}>
                                {employee?.account_number || employee?.bank_account_number || "N/A"}
                            </div>
                        </div>
                        <div>
                            <div style={S.label}>UAN</div>
                            <div style={S.value}>{employee?.uan_number || employee?.uan || "N/A"}</div>
                        </div>
                        <div>
                            <div style={S.label}>PF Number</div>
                            <div style={S.value}>{employee?.pf_number || "N/A"}</div>
                        </div>
                        <div>
                            <div style={S.label}>PAN Number</div>
                            <div style={S.value}>{employee?.pan_number || employee?.panNumber || "N/A"}</div>
                        </div>
                    </div>
                </div>

                <hr style={S.hr} />

                {/* SALARY DETAILS */}
                <div style={S.sectionTitle}>SALARY DETAILS</div>
                <hr style={S.hr} />

                <div style={S.salaryRow}>
                    <div>
                        <div style={S.label}>Actual Payable Days</div>
                        <div style={S.value}>{Number(payableDays || 0).toFixed(1)}</div>
                    </div>
                    <div>
                        <div style={S.label}>Total Working Days</div>
                        <div style={S.value}>{Number(workingDays || 0).toFixed(1)}</div>
                    </div>
                    <div>
                        <div style={S.label}>Loss Of Pay Days</div>
                        <div style={S.value}>{formatINR(lop || 0)}</div>
                    </div>
                    <div>
                        <div style={S.label}>Days Payable</div>
                        <div style={S.value}>{Math.round(Number(payableDays || 0))}</div>
                    </div>
                </div>

                <hr style={S.lightHr} />

                {/* Earnings + Deductions side-by-side with middle divider */}
                <div style={S.splitWrap}>
                    {/* Earnings */}
                    <div>
                        <div style={S.blockTitle}>EARNINGS</div>
                        <div style={S.list}>
                            {earningsEntries.map(([name, amt], idx) => (
                                <div key={idx} style={S.itemRow}>
                                    <div style={S.itemName}>{name}</div>
                                    <div style={S.itemAmt}>{formatINR(amt)}</div>
                                </div>
                            ))}

                            <div style={S.itemRow}>
                                <div style={S.totalName}>Total Earnings (A)</div>
                                <div style={S.totalAmt}>{formatINR(totalEarnings)}</div>
                            </div>
                        </div>
                    </div>

                    {/* Vertical divider */}
                    <div style={S.vLine} />

                    {/* Deductions */}
                    <div>
                        <div style={S.blockTitle}>TAXES &amp; DEDUCTIONS</div>
                        <div style={S.list}>
                            {deductionsEntries.length > 0 ? (
                                deductionsEntries.map(([name, amt], idx) => (
                                    <div key={idx} style={S.itemRow}>
                                        <div style={S.itemName}>{name}</div>
                                        <div style={S.itemAmt}>{formatINR(amt)}</div>
                                    </div>
                                ))
                            ) : (
                                <div style={S.itemRow}>
                                    <div style={S.itemName}>N/A</div>
                                    <div style={S.itemAmt}>{formatINR(0)}</div>
                                </div>
                            )}

                            <div style={S.itemRow}>
                                <div style={S.totalName}>Total Taxes &amp; Deductions (B)</div>
                                <div style={S.totalAmt}>{formatINR(totalDeductions)}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Net salary grey box */}
                <div style={S.netBox}>
                    <div style={S.netRow}>
                        <div style={S.netLabel}>Net Salary Payable ( A - B )</div>
                        <div style={S.netValue}>{formatINR(netSalary)}</div>
                    </div>

                    <div style={S.netRow}>
                        <div style={S.netWordsLabel}>Net Salary in words</div>
                        <div style={S.netWordsValue}>{netSalaryWords} Rupees only</div>
                    </div>
                </div>

                {/* Notes */}
                <div style={S.notesWrap}>
                    <div style={S.noteLine}>
                        <span style={S.noteBold}>**Note :</span>
                        <span style={S.noteItalic}>All amounts displayed in this payslip are in INR</span>
                    </div>

                    <div style={S.footerItalic}>
                        *This is computer generated statement, does not require signature.
                    </div>
                </div>
            </div>
        );
    };

    if (!companySettings || !companySettings.name) {
        return (
            <div className={`fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4`}>
                <div className={`rounded-xl shadow-2xl max-w-2xl w-full p-6 ${
                    theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                }`}>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            Company Settings Required
                        </h2>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                        Please complete company settings with address and logo to generate payslips.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className={`rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            }`}>
                {/* Modal Header */}
                <div className={`p-4 border-b flex items-center justify-between flex-shrink-0 ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                    <div>
                        <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            Payslip - {monthYear}
                        </h2>
                        <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            Template: {selectedTemplate === 'pay-standard' ? 'Standard' : selectedTemplate === 'pay-detailed' ? 'Detailed Breakdown' : selectedTemplate === 'pay-modern' ? 'Modern Visual' : 'Cognizant Style'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handlePrint}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                            title="Print"
                        >
                            <Printer className="w-5 h-5" />
                        </button>
                        <button
                            onClick={handleDownload}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                            title="Download"
                        >
                            <Download className="w-5 h-5" />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Payslip Content - Always White Background - Scrollable */}
                <div id="payslip-content" className="p-8 bg-white text-gray-900 overflow-y-auto flex-1">
                    {/* Render template based on selection */}
                    {selectedTemplate === 'pay-standard' && renderStandardTemplate()}
                    {selectedTemplate === 'pay-detailed' && renderDetailedTemplate()}
                    {selectedTemplate === 'pay-modern' && renderModernTemplate()}
                    {selectedTemplate === 'pay-cognizant' && renderCognizantTemplate()}
                </div>
            </div>
        </div>
    );
};

export default Payslip;
